import { useState, useCallback, useMemo } from 'react';
import { extractPagedResponse } from '../utils/responseUtils';

const DEFAULT_PAGE_SIZE = 20;

/**
 * Shared pagination hook for all list pages.
 *
 * @param {number} [pageSize=20] - Items per page
 * @returns Pagination state and helpers
 */
export const usePagination = (pageSize = DEFAULT_PAGE_SIZE) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const totalPages = useMemo(
    () => Math.ceil(totalCount / pageSize) || 1,
    [totalCount, pageSize]
  );

  const goToPage = useCallback(
    (page) => {
      const p = Math.max(1, Math.min(page, totalPages));
      setCurrentPage(p);
    },
    [totalPages]
  );

  const nextPage = useCallback(() => goToPage(currentPage + 1), [currentPage, goToPage]);
  const prevPage = useCallback(() => goToPage(currentPage - 1), [currentPage, goToPage]);
  const resetPage = useCallback(() => setCurrentPage(1), []);

  /** Query params object ready to spread into API calls */
  const paginationParams = useMemo(
    () => ({ page: currentPage, pageSize }),
    [currentPage, pageSize]
  );

  /** Delegates to shared utility – returns { items, totalCount } */
  const extractPagedData = useCallback(
    (response) => extractPagedResponse(response),
    []
  );

  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalCount);

  return {
    currentPage,
    totalCount,
    totalPages,
    pageSize,
    startIndex,
    endIndex,
    goToPage,
    nextPage,
    prevPage,
    resetPage,
    setTotalCount,
    paginationParams,
    extractPagedData,
  };
};
